// STEP OMS-UI-3A · 상황별 카톡 안내 · Foundation (FRONTEND ONLY · NO SEND)
// - 선택된 seller_order_id 각각에 대해 전체 items 를 GET /seller/orders/{sid}/unified 로 로드.
//   현재 stage 필터와 무관하게 주문의 모든 상품이 표시됨 (UnifiedOrderDetail 과 동일 source).
// - 3개 안내 유형 (LM_SHIPPING_DELAY · LM_PARTIAL_SHIPMENT · LM_OUT_OF_STOCK) 은 UI placeholder.
//   BizM 검수 중 · registry 연결 없음 · 실제 발송 button/api 없음.
// - 주문별 상품 복수선택 → future variable preview (product_name / shipped_product_name).
// - LOCK: 새 backend/API 없음.  Kakao P4B/BizM/state machine/inventory 무변경.
import React, { useEffect, useMemo, useState } from 'react';
import { toast } from 'sonner';
import { X as XIcon, Loader2, Package, MessageSquare } from 'lucide-react';
import api from '@/lib/api';

// 사람이 읽을 수 있는 옵션 문자열 (UnifiedOrderDetail 규칙 REUSE · raw variant_key 금지).
const _renderOption = (opt) => {
  if (opt && typeof opt === 'object') {
    return Object.entries(opt)
      .filter(([, v]) => v !== null && v !== '')
      .map(([, v]) => v).join(' / ');
  }
  return typeof opt === 'string' ? opt : '';
};

// 안내 유형 · registry 연결 없음 (UI placeholder).
export const GUIDE_TYPES = [
  {
    key: 'shipping_delay',
    label: '입고·배송 지연',
    template_code_future: 'LM_SHIPPING_DELAY',
    var_key_future: 'product_name',
    desc: '입고/배송 지연으로 안내가 필요한 주문 상품을 선택하세요.',
  },
  {
    key: 'partial_shipment',
    label: '일부상품 선발송',
    template_code_future: 'LM_PARTIAL_SHIPMENT',
    var_key_future: 'shipped_product_name',
    desc: '먼저 발송한 상품을 선택하세요. 남은 상품은 추후 발송됩니다.',
  },
  {
    key: 'out_of_stock',
    label: '품절·변경 안내',
    template_code_future: 'LM_OUT_OF_STOCK',
    var_key_future: 'product_name',
    desc: '품절/옵션 변경이 발생한 상품을 선택하세요.',
  },
];

export default function KakaoContextGuideModal({ open, onClose, sellerOrderIds }) {
  const [guideKey, setGuideKey] = useState('shipping_delay');
  const [loading, setLoading] = useState(false);
  const [orders, setOrders] = useState([]);        // [{seller_order_id, order_number, items:[]}]
  const [errored, setErrored] = useState([]);      // [{sid, reason}]
  // 주문별 선택 · { [sid]: Set<itemIndex> }
  const [picks, setPicks] = useState({});

  const ids = useMemo(
    () => Array.from(sellerOrderIds || []),
    [sellerOrderIds],
  );

  // Modal 열릴 때마다 stale selection reset · 주문 items 재조회.
  useEffect(() => {
    if (!open) return;
    setPicks({});
    setGuideKey('shipping_delay');
    setErrored([]);
    if (ids.length === 0) { setOrders([]); return; }
    setLoading(true);
    (async () => {
      const results = await Promise.all(ids.map(async (sid) => {
        try {
          const r = await api.get(`/seller/orders/${sid}/unified`);
          return { ok: true, sid, data: r.data };
        } catch (e) {
          return { ok: false, sid, reason: e?.response?.data?.detail || '주문 조회 실패' };
        }
      }));
      const okList = results.filter((x) => x.ok).map((x) => ({
        seller_order_id: x.sid,
        order_number:    x.data?.order_number || x.sid,
        items:           Array.isArray(x.data?.items) ? x.data.items : [],
      }));
      const failList = results.filter((x) => !x.ok).map((x) => ({ sid: x.sid, reason: x.reason }));
      setOrders(okList);
      setErrored(failList);
      if (failList.length) {
        toast.error(`주문 ${failList.length}건 불러오기 실패`);
      }
      setLoading(false);
    })();
  }, [open, ids]);

  if (!open) return null;

  const currentGuide = GUIDE_TYPES.find((g) => g.key === guideKey) || GUIDE_TYPES[0];

  const toggleItem = (sid, idx) => {
    setPicks((prev) => {
      const next = { ...prev };
      const set = new Set(next[sid] || []);
      if (set.has(idx)) set.delete(idx); else set.add(idx);
      next[sid] = set;
      return next;
    });
  };
  const toggleAllForOrder = (sid, itemCount) => {
    setPicks((prev) => {
      const next = { ...prev };
      const set = new Set(next[sid] || []);
      if (set.size === itemCount) next[sid] = new Set();
      else next[sid] = new Set(Array.from({ length: itemCount }, (_, i) => i));
      return next;
    });
  };

  // 주문별 preview: "롱코트(블랙/M) / 니트(아이보리/FREE)" 형태.
  const renderPreviewForOrder = (order) => {
    const set = picks[order.seller_order_id] || new Set();
    const selectedItems = order.items.filter((_, i) => set.has(i));
    if (!selectedItems.length) return '';
    return selectedItems.map((it) => {
      const name = it.product_name_snapshot || it.name_snapshot || it.name || '상품';
      const opt = _renderOption(it.option);
      return opt ? `${name}(${opt})` : name;
    }).join(' / ');
  };

  const totalSelectedCount = orders.reduce((acc, o) => acc + (picks[o.seller_order_id]?.size || 0), 0);
  const orderHasAnyPick = (sid) => (picks[sid]?.size || 0) > 0;

  return (
    <div className="fixed inset-0 z-[110] bg-black/60 flex items-center justify-center p-4"
         onClick={onClose} data-testid="kakao-guide-overlay">
      <div className="bg-white dark:bg-[#141414] rounded-xl w-full max-w-3xl max-h-[92vh] overflow-hidden flex flex-col shadow-2xl border border-black/10 dark:border-white/10"
           onClick={(e) => e.stopPropagation()} data-testid="kakao-guide-modal">
        {/* Header */}
        <div className="px-5 py-3.5 border-b border-black/10 dark:border-white/10 flex items-center justify-between">
          <div className="flex items-center gap-2 min-w-0">
            <MessageSquare size={16} className="text-[#FF3B30] flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-black truncate" style={{ fontFamily: 'Manrope, sans-serif' }}>
                상황별 카톡 안내
              </h3>
              <p className="text-[11px] text-gray-500">
                선택 주문 <b className="text-black dark:text-white tabular-nums">{ids.length}</b>건 ·
                유형과 대상 상품을 선택하세요 · <b className="text-amber-600">실제 발송 없음 (검수 준비 단계)</b>
              </p>
            </div>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-[#FF3B30]" aria-label="닫기"
                  data-testid="kakao-guide-close">
            <XIcon size={18} />
          </button>
        </div>

        {/* Type Tabs */}
        <div className="px-5 pt-3 pb-2 border-b border-black/5 dark:border-white/5">
          <div className="flex flex-wrap gap-1.5" data-testid="kakao-guide-type-tabs">
            {GUIDE_TYPES.map((g) => (
              <button key={g.key} onClick={() => setGuideKey(g.key)}
                      className={`px-3 py-1.5 rounded-full text-[12px] font-bold border transition ${
                        guideKey === g.key
                          ? 'bg-[#FF3B30] text-white border-[#FF3B30]'
                          : 'bg-white dark:bg-[#0f0f0f] border-black/10 dark:border-white/10 hover:border-[#FF3B30]/50'
                      }`}
                      data-testid={`kakao-guide-type-${g.key}`}>
                {g.label}
              </button>
            ))}
          </div>
          <p className="mt-2 text-[11px] text-gray-500" data-testid="kakao-guide-type-desc">
            {currentGuide.desc}
            <span className="ml-1 text-gray-400">
              · 예정 template <b className="font-mono">{currentGuide.template_code_future}</b>
              · 변수 <b className="font-mono">#{`{${currentGuide.var_key_future}}`}</b>
            </span>
          </p>
        </div>

        {/* Body · 주문별 카드 */}
        <div className="flex-1 overflow-y-auto px-5 py-4 space-y-3" data-testid="kakao-guide-body">
          {loading ? (
            <div className="flex items-center justify-center gap-2 text-sm text-gray-500 py-10">
              <Loader2 size={16} className="animate-spin" /> 주문 상품 불러오는 중…
            </div>
          ) : orders.length === 0 ? (
            <div className="text-center text-sm text-gray-400 py-10">
              불러올 주문이 없습니다.
            </div>
          ) : (
            <>
              {errored.length > 0 && (
                <div className="rounded-lg border border-amber-300/60 bg-amber-50 dark:bg-amber-500/10 p-2 text-[11px] text-amber-800 dark:text-amber-200"
                     data-testid="kakao-guide-errored">
                  주문 {errored.length}건 조회 실패 · 다시 시도해 주세요.
                </div>
              )}
              {orders.map((o) => {
                const set = picks[o.seller_order_id] || new Set();
                const allSelected = o.items.length > 0 && set.size === o.items.length;
                return (
                  <div key={o.seller_order_id}
                       className="rounded-lg border border-black/10 dark:border-white/10 bg-white dark:bg-[#0f0f0f]"
                       data-testid={`kakao-guide-order-${o.seller_order_id}`}>
                    <div className="px-3 py-2 flex items-center justify-between gap-2 border-b border-black/5 dark:border-white/5">
                      <div className="min-w-0">
                        <p className="text-[11px] text-gray-500">주문번호</p>
                        <p className="text-sm font-black truncate font-mono"
                           data-testid={`kakao-guide-order-no-${o.seller_order_id}`}>
                          {o.order_number}
                        </p>
                      </div>
                      <label className="flex items-center gap-1 text-[11px] font-bold cursor-pointer flex-shrink-0">
                        <input type="checkbox" checked={allSelected}
                               onChange={() => toggleAllForOrder(o.seller_order_id, o.items.length)}
                               disabled={o.items.length === 0}
                               className="accent-[#FF3B30]"
                               data-testid={`kakao-guide-order-select-all-${o.seller_order_id}`} />
                        전체 선택 <span className="text-gray-400">({set.size}/{o.items.length})</span>
                      </label>
                    </div>
                    <div className="p-2 space-y-1.5" data-testid={`kakao-guide-items-${o.seller_order_id}`}>
                      {o.items.length === 0 ? (
                        <p className="text-center text-xs text-gray-400 py-3">상품 정보가 없습니다.</p>
                      ) : o.items.map((it, i) => {
                        const img = it.image_snapshot || it.image || it.thumbnail || '';
                        const name = it.product_name_snapshot || it.name_snapshot || it.name || '-';
                        const opt = _renderOption(it.option);
                        const qty = Number(it.quantity || 0) || 0;
                        const checked = set.has(i);
                        return (
                          <label key={`${o.seller_order_id}-${i}`}
                                 className={`flex items-center gap-2 text-xs p-2 rounded border cursor-pointer transition ${
                                   checked
                                     ? 'border-[#FF3B30]/60 bg-[#FF3B30]/5'
                                     : 'border-black/5 dark:border-white/5 hover:border-[#FF3B30]/30'
                                 }`}
                                 data-testid={`kakao-guide-item-${o.seller_order_id}-${i}`}>
                            <input type="checkbox" checked={checked}
                                   onChange={() => toggleItem(o.seller_order_id, i)}
                                   className="accent-[#FF3B30]"
                                   data-testid={`kakao-guide-item-check-${o.seller_order_id}-${i}`} />
                            <div className="flex-shrink-0 w-10 h-10 rounded overflow-hidden bg-black/5 dark:bg-white/5 border border-black/5 dark:border-white/5 flex items-center justify-center">
                              {img
                                ? <img src={img} alt="" className="w-full h-full object-cover" loading="lazy"
                                       onError={(e) => { e.currentTarget.style.display = 'none'; }} />
                                : <Package size={14} className="text-gray-300" />}
                            </div>
                            <div className="min-w-0 flex-1">
                              <p className="font-semibold truncate"
                                 data-testid={`kakao-guide-item-name-${o.seller_order_id}-${i}`}>
                                {name}
                                {opt && <span className="text-gray-400 font-normal"> · {opt}</span>}
                              </p>
                              <p className="text-[10px] text-gray-500">수량 × {qty}</p>
                            </div>
                          </label>
                        );
                      })}
                    </div>
                    {/* 주문별 Preview */}
                    <div className="px-3 py-2 border-t border-black/5 dark:border-white/5 bg-black/5 dark:bg-white/5">
                      <p className="text-[10px] font-black uppercase tracking-widest text-gray-500">
                        Preview · <span className="font-mono text-gray-600 dark:text-gray-300">
                          #{`{${currentGuide.var_key_future}}`}
                        </span>
                      </p>
                      <p className="mt-0.5 text-[12px] font-semibold text-gray-800 dark:text-gray-100 break-words"
                         data-testid={`kakao-guide-preview-${o.seller_order_id}`}>
                        {orderHasAnyPick(o.seller_order_id)
                          ? renderPreviewForOrder(o)
                          : <span className="text-gray-400 font-normal">상품을 선택하면 여기에 표시됩니다.</span>}
                      </p>
                    </div>
                  </div>
                );
              })}
            </>
          )}
        </div>

        {/* Footer */}
        <div className="px-5 py-3 border-t border-black/10 dark:border-white/10 flex items-center justify-between gap-2"
             data-testid="kakao-guide-footer">
          <p className="text-[11px] text-gray-500">
            선택 상품 <b className="text-black dark:text-white tabular-nums"
                        data-testid="kakao-guide-total-count">{totalSelectedCount}</b>개
            <span className="ml-2 text-amber-600 font-bold">발송 없음 · Foundation only</span>
          </p>
          <div className="flex items-center gap-2">
            <button onClick={onClose}
                    className="px-3 py-1.5 rounded-full border border-black/10 dark:border-white/10 text-xs font-bold"
                    data-testid="kakao-guide-close-btn">
              닫기
            </button>
            <button disabled title="다음 STEP (OMS-UI-3B) 에서 지연 예정일/선발송 송장/품절 문의 연결과 함께 실사용됩니다."
                    className="px-3 py-1.5 rounded-full bg-[#FF3B30]/40 text-white text-xs font-black cursor-not-allowed"
                    data-testid="kakao-guide-next-disabled">
              다음 · Foundation
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
